/* eslint-disable prettier/prettier */
import { useState, useCallback } from 'react';
import {
	courseEnrollmentService,
	CreateEnrollmentPayload,
	UpdateEnrollmentPayload,
	BulkEnrollPayload,
} from '../services/courseEnrollmentService';

export const useCourseEnrollment = () => {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const [isDeleting, setIsDeleting] = useState(false);
	const [successMessage, setSuccessMessage] = useState<string | null>(null);

	const [createSuccess, setCreateSuccess] = useState(false);
	const [createdEnrollment, setCreatedEnrollment] = useState<any>(null);

	const handleRequest = useCallback(async <T>(request: () => Promise<T>) => {
		try {
			setLoading(true);
			setError(null);
			const response = await request();
			return response;
		} catch (err: any) {
			const message = err?.message || 'Something went wrong';
			setError(message);
			throw err;
		} finally {
			setLoading(false);
		}
	}, []);

	// ---- Actions ----

	// ===============================
	// 🔹 Create Enrollment
	// ===============================
	const createEnrollment = async (payload: CreateEnrollmentPayload) => {
		setLoading(true);
		setError(null);
		setCreateSuccess(false);

		try {
			const res = await courseEnrollmentService.createEnrollment(payload);

			if (res.success) {
				setCreatedEnrollment(res.data);
				setCreateSuccess(true);
				return res.data;
			} else {
				setError(res.message || 'Failed to create enrollment');
				return null;
			}
		} catch (err: any) {
			console.error('Hook createEnrollment error:', err);
			setError(err.message || 'Something went wrong');
			return null;
		} finally {
			setLoading(false);
		}
	};

	const getEnrollmentById = (id: string) =>
		handleRequest(() => courseEnrollmentService.getEnrollmentById(id));

	const getUserEnrollments = (status?: string) =>
		handleRequest(() => courseEnrollmentService.getUserEnrollments(status));

	const getCourseEnrollments = (courseId: string) =>
		handleRequest(() =>
			courseEnrollmentService.getCourseEnrollments(courseId)
		);

	const updateEnrollment = (id: string, payload: UpdateEnrollmentPayload) =>
		handleRequest(() =>
			courseEnrollmentService.updateEnrollment(id, payload)
		);

	const deleteEnrollment = async (enrollmentId: string) => {
		setIsDeleting(true);
		setError(null);
		setSuccessMessage(null);

		try {
			await courseEnrollmentService.deleteEnrollment(enrollmentId);
			setSuccessMessage('Enrollment deleted successfully');
		} catch (err: any) {
			setError(err.message || 'Failed to delete enrollment');
		} finally {
			setIsDeleting(false);
		}
	};

	const getEnrollmentStats = (courseId: string) =>
		handleRequest(() =>
			courseEnrollmentService.getEnrollmentStats(courseId)
		);

	const bulkEnroll = (payload: BulkEnrollPayload) =>
		handleRequest(() => courseEnrollmentService.bulkEnroll(payload));

	return {
		loading,
		error,

		getEnrollmentById,
		getUserEnrollments,
		getCourseEnrollments,
		updateEnrollment,
		deleteEnrollment,
		getEnrollmentStats,
		bulkEnroll,
		isDeleting,
		successMessage,
		createEnrollment, // ✅ ADD
		createSuccess, // ✅ ADD
		createdEnrollment, // ✅ ADD
	};
};
